/**
 * Audio Utilities
 *
 * Provides audio playback helpers for UI sounds.
 * Note: Currently unused but kept for potential future features.
 */

import { logWarning } from "./logger";

// Singleton AudioContext - created on first user interaction for iOS compatibility
let sharedAudioContext: AudioContext | null = null;

function getOrCreateAudioContext(): AudioContext | null {
  if (sharedAudioContext) {
    // Resume if suspended
    if (sharedAudioContext.state === "suspended") {
      sharedAudioContext.resume();
    }
    return sharedAudioContext;
  }
  return null;
}

// Initialize audio on user gesture - must be called during touch/click handler
function initAudioOnGesture() {
  if (sharedAudioContext) {
    // Already initialized, just resume if needed
    if (sharedAudioContext.state === "suspended") {
      sharedAudioContext.resume();
    }
    return;
  }

  // Create AudioContext during user gesture (required for iOS)
  try {
    sharedAudioContext = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext)();

    // Play silent buffer to fully unlock on iOS
    const buffer = sharedAudioContext.createBuffer(1, 1, 22050);
    const source = sharedAudioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(sharedAudioContext.destination);
    source.start(0);
  } catch (e) {
    logWarning("Failed to initialize audio:", e);
  }
}

// Set up listeners to init audio on first interaction
if (typeof document !== "undefined") {
  const initHandler = () => initAudioOnGesture();
  document.addEventListener("touchstart", initHandler, { passive: true });
  document.addEventListener("touchend", initHandler, { passive: true });
  document.addEventListener("click", initHandler);
  // Also try on scroll for swipe gestures
  document.addEventListener("scroll", initHandler, {
    passive: true,
    once: true,
  });
}

/**
 * Play typewriter click sound with optional haptic feedback
 * Currently unused but available for future features
 */
export function playTypewriterClick(): void {
  // Subtle haptic feedback (very short vibration)
  if (navigator.vibrate) {
    navigator.vibrate(1); // 1ms pulse - very subtle
  }

  const ctx = getOrCreateAudioContext();
  if (!ctx || ctx.state !== "running") return;

  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Short, punchy click sound
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(
      200,
      ctx.currentTime + 0.02,
    );

    // Quick attack and decay (subtle volume)
    gainNode.gain.setValueAtTime(0.01, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.03);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.03);
  } catch {
    // Silently fail if audio doesn't work
  }
}
